/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef } from 'react';
import type {
  ReactElement,
  ReactNode,
  Ref,
} from 'react';

import type {
  GroupBase,
} from 'react-select';

import type {
  UseSelectFetchParams,
} from 'react-select-fetch';
import type {
  ComponentProps,
} from 'react-select-async-paginate';

import { SelectBase } from '../SelectBase';

import { texts } from '../ReactSelectExtended/texts';
import {
  useStyles,
} from '../ReactSelectExtended/styles';
import { selectComponentsAsync } from '../ReactSelectExtended/components';
import type {
  OptionTypeBase,
  SelectBaseProps,
} from '../ReactSelectExtended/types';

import {
  SelectFetchExtended,
} from '../ReactSelectExtended/ReactSelectExtended';
import type {
  ReactSelectExtendedProps,
} from '../ReactSelectExtended/ReactSelectExtended';

export type SelectAjaxProps<
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
IsMulti extends boolean,
> =
  & ReactSelectExtendedProps<OptionType, IsMulti, Group>
  & UseSelectFetchParams<OptionType, Group>
  & ComponentProps<OptionType, Group, IsMulti>
  & Omit<SelectBaseProps<OptionType, Group, IsMulti>, 'defaultComponents' | 'children'>;

const defaultQueryParams = {};

function SelectAjaxComponent<
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
IsMulti extends boolean,
>(
  props: SelectAjaxProps<OptionType, Group, IsMulti>,
  ref: Ref<HTMLDivElement>,
): ReactElement {
  const {
    get = undefined,
    hasError = false,
    hasWarning = false,
    isClearable = true,
    isMulti = false,
    small = false,
    mapResponse = undefined,
    renderModal = undefined,
    offsetParamName = null,
    pageParamName = 'page',
    searchParamName = 'q',
    selectRef = null,
    queryParams = defaultQueryParams,
    ...restProps
  } = props;

  const styles = useStyles(small);

  return (
    <SelectBase
      defaultComponents={selectComponentsAsync}
      {...props}
      ref={ref}
    >
      {({
        getOptionLabel,
        getOptionValue,
        components,
      }): ReactNode => (
        <SelectFetchExtended
          {...texts}
          isMulti={isMulti}
          hasError={hasError}
          hasWarning={hasWarning}
          small={small}
          closeMenuOnSelect={!isMulti}
          isArrowHidden={Boolean(renderModal)}
          components={components}
          styles={styles}
          {...restProps}
          get={get}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          isClearable={isClearable}
          mapResponse={mapResponse}
          offsetParamName={offsetParamName}
          pageParamName={pageParamName}
          queryParams={queryParams}
          searchParamName={searchParamName}
          selectRef={selectRef}
        />
      )}
    </SelectBase>
  );
}

export const SelectAjax = forwardRef(SelectAjaxComponent) as <
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
IsMulti extends boolean,
>(
  props: SelectAjaxProps<OptionType, Group, IsMulti> & {
    ref?: Ref<HTMLDivElement>;
  },
) => ReactElement;
